import type { Schema } from '@dealdesk/api-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { CheckboxField, DateField, FileField, FormErrorSummary, MoneyField, SelectField, TextareaField, applyServerErrors, zMoney } from '@/business';
import { Button, Dialog, DialogContent, toast } from '@/design-system';
import { apiErrorMessage, isApiError, type ApiError } from '@/shared/api';
import { todayInDubai } from '@/shared/lib/dates';

import { useSetShortfall } from '../../api/mutations';

const schema = z.object({ amount: zMoney({ min: '1.00' }), fundedBy: z.enum(['customer', 'broker', 'bank', 'other']), status: z.enum(['pending', 'funded', 'waived']), dueOn: z.string().optional(), blocking: z.boolean(), note: z.string().optional() });
type Values = z.infer<typeof schema>;

/** Records or updates the shortfall. Marking it funded needs proof (a file). The customer funds it by default. */
export function ShortfallDialog({ dealId, shortfall, onClose }: { dealId: string; shortfall: Schema<'Shortfall'> | null; onClose: () => void }) {
  const { t } = useTranslation('deals');
  const save = useSetShortfall(dealId);
  const [problem, setProblem] = useState<ApiError | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { amount: shortfall?.amount ?? '', fundedBy: shortfall?.fundedBy ?? 'customer', status: shortfall?.status ?? 'pending', dueOn: shortfall?.dueOn ?? '', blocking: shortfall?.blocking ?? true, note: shortfall?.note ?? '' } });
  const status = useWatch({ control: form.control, name: 'status' });
  const submit = form.handleSubmit(async (v) => {
    setProblem(null);
    try {
      await save.mutateAsync({ body: { amount: v.amount, fundedBy: v.fundedBy, status: v.status, blocking: v.blocking, dueOn: v.dueOn || null, ...(v.note?.trim() ? { note: v.note.trim() } : {}) }, file });
      toast.done(t('settlement.shortfallSaved'));
      onClose();
    } catch (e) {
      if (isApiError(e)) {
        applyServerErrors(form, e, ['amount', 'dueOn']);
        setProblem(e);
      }
    }
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        size="form"
        title={shortfall ? t('settlement.updateShortfall') : t('settlement.recordShortfall')}
        description={t('settlement.shortfallBody')}
        footer={
          <>
            <Button onClick={onClose}>{t('actions.cancel', { ns: 'common' })}</Button>
            <Button variant="primary" loading={save.isPending} onClick={() => void submit()}>
              {t('settlement.saveShortfall')}
            </Button>
          </>
        }
      >
        <form onSubmit={(e) => void submit(e)} noValidate className="flex flex-col gap-4">
          {problem ? <FormErrorSummary title={apiErrorMessage(problem)} /> : null}
          <div className="grid grid-cols-2 gap-4">
            <MoneyField control={form.control} name="amount" label={t('workflow.shortfall.amount', { ns: 'common' })} />
            <SelectField control={form.control} name="fundedBy" label={t('workflow.shortfall.fundedBy', { ns: 'common' })} options={(['customer', 'broker', 'bank', 'other'] as const).map((f) => ({ value: f, label: t(`workflow.shortfall.funder.${f}`, { ns: 'common' }) }))} />
            <SelectField control={form.control} name="status" label={t('workflow.shortfall.status', { ns: 'common' })} options={(shortfall?.status === 'waived' ? (['pending', 'funded', 'waived'] as const) : (['pending', 'funded'] as const)).map((s) => ({ value: s, label: t(`shortfall.${s}`, { ns: 'status' }) }))}
              help={shortfall?.status === 'waived' ? undefined : t('settlement.waiver.statusHelp')} />
            <DateField control={form.control} name="dueOn" label={t('workflow.shortfall.due', { ns: 'common' })} optional min={todayInDubai()} />
          </div>
          <CheckboxField control={form.control} name="blocking" label={t('settlement.blocking')} help={t('settlement.blockingHelp')} />
          {status === 'funded' ? (
            <FileField
              label={t('workflow.shortfall.evidence', { ns: 'common' })}
              purpose="shortfall_evidence"
              optional={Boolean(shortfall?.evidenceFileName)}
              help={shortfall?.evidenceFileName ? t('settlement.evidenceOnFile', { name: shortfall.evidenceFileName }) : t('settlement.evidenceHelp')}
              onChange={setFile}
            />
          ) : null}
          <TextareaField control={form.control} name="note" label={t('settlement.fields.note')} optional rows={2} />
          <button type="submit" hidden />
        </form>
      </DialogContent>
    </Dialog>
  );
}
